import type { FC, JSX } from "react";
import { useRef, useState } from "react";

import type { AnyKindOfDictionary } from "lodash";
import { useSelector } from "react-redux";

import { DotsLoader, FakeInput, Tooltip } from "@similarweb/ui-components";

import { useAutoCompleteClick } from "pro-features/components/AutocompleteWebCategories/utils";
import { DEFAULT_SELECTION_TYPE_PROPS } from "pro-features/components/AutocompleteWithTabs/const";
import { translate } from "app/filters/i18nFilter";
import { ListItemSegment } from "pro-features/pages/segments/components/ListItems";
import { ListItemSegmentWithCheckbox } from "pro-features/pages/segments/components/ListItemSegmentWithCheckbox";
import { EMPTY_CUSTOM_SEGMENTS } from "app/services/segments/segmentsApiService";
import { fetchSimilarSitesQuery } from "app/services/sitesService/queries/sitesQueries";
import { getUrlDomain, isValidUrl } from "app/services/sitesService/sitesUtils";
import { getWebsiteResults } from "app/services/solutions2Services/HomepageDataFetchers/NewModulesHomepageDataFetcher";
import { EAssetType } from "app/UtilitiesAndConstants/types/assetType";

import { addItemToRecentSearches, getDisabledState, getListItems, hasQuery, type IListItem } from "./handlers";
import { AutocompleteStyled, FakeInputContainerStyled } from "./styled";
import { Tabs } from "./Tabs";
import type { IAsset, ISelectionTypeProps, ITabsProps, IWebsiteResult, SelectedItemsView } from "./types";
import { ESelectionType } from "./types";

interface IAutocompleteWithTabsProps {
    selectedValue?: IAsset;
    onSelect: (selectedValue: IAsset | Array<IAsset>) => void;
    excludeItems?: IAsset[];
    selectedMainAsset?: IAsset;
    autoFocus?: boolean;
    disabled?: boolean;
    searchIcon?: string;
    closeButtonIconColor?: string;
    hasBorder?: boolean;
    includeQueryAsItem?: boolean;
    shouldDisableSegmentsTab?: () => boolean;
    autocompleteProps?: AnyKindOfDictionary;
    isDisabledListItemSupported?: boolean; // should avoid segments and domains overlap
    tabsProps?: ITabsProps;
    selectionTypeProps?: ISelectionTypeProps;
    placeholder?: string | JSX.Element;
    onBlur?: (e: FixmeType) => void;
    showLinesOfBusiness?: boolean;
}

export const AutocompleteWithTabs: FC<IAutocompleteWithTabsProps> = ({
    selectedValue,
    onSelect,
    excludeItems = [],
    selectedMainAsset,
    autoFocus,
    disabled,
    searchIcon = "search",
    closeButtonIconColor,
    hasBorder,
    includeQueryAsItem = false,
    shouldDisableSegmentsTab,
    autocompleteProps,
    isDisabledListItemSupported = false,
    tabsProps = {},
    selectionTypeProps = DEFAULT_SELECTION_TYPE_PROPS,
    placeholder = translate("competitive.tracker.autocomplete.placeholder"),
    onBlur,
    showLinesOfBusiness,
}) => {
    const { selectionTypes, callbackOnCheckboxClick } = selectionTypeProps;

    const autocompleteRef = useRef<FixmeType>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [websiteResults, setWebsiteResults] = useState<FixmeType[]>([]);
    const [selectedItemsView, setSelectedItemsView] = useState<SelectedItemsView>({});
    // Track the latest debounced query for LOB fetching (independent of website fetch timing)
    const [lobQuery, setLobQuery] = useState<string>("");

    const { segmentsLoading, customSegmentsMeta = EMPTY_CUSTOM_SEGMENTS } = useSelector((state) => {
        const {
            segmentsModule: { segmentsLoading, customSegmentsMeta },
        } = state;
        return { segmentsLoading, customSegmentsMeta };
    });

    useAutoCompleteClick(autocompleteRef);

    const onClearSearch = () => {
        // @ts-ignore FIXME: strict migration - Argument 'undefined' not assignable to 'IAsset[] | IAsset' — clear action intentionally calls onSelect with no value
        onSelect(undefined);
    };

    const renderItemsClick = (item: IListItem) => () => {
        autocompleteRef.current.truncateResults(true);
        if (item.type === EAssetType.Website) {
            addItemToRecentSearches(item);
        }
        onSelect(item);
        autocompleteRef.current.textFieldRef.clearValue();
        resetSelectedItemsView();
    };

    const getWebsiteResultsWithQuery = (websiteResults: IWebsiteResult[], query: string): IWebsiteResult[] => {
        let domainIcon = null;
        const queryDomain = getUrlDomain(query);

        const isInWebsiteResults = websiteResults?.filter((website) => {
            if (queryDomain === getUrlDomain(website.name)) {
                domainIcon = website.image;
            }
            return website.name === query;
        }).length;

        const doAddItem = !isInWebsiteResults && isValidUrl(query);
        if (doAddItem) {
            const websiteItem = { name: query, image: domainIcon };
            // @ts-ignore FIXME: strict migration - '{ name: string; image: null }' not assignable to 'IWebsiteResult' — manually-added query item has null image when domain is unmatched
            websiteResults.unshift(websiteItem);
        }

        return websiteResults;
    };

    const onApplyMultiSelect = () => {
        autocompleteRef.current.truncateResults(true);
        autocompleteRef.current.textFieldRef.clearValue();
        const selectedValues = Object.values(selectedItemsView);
        onSelect(selectedValues);
        resetSelectedItemsView();
    };

    const onItemClickWithMultiselect = (item: IListItem) => {
        const { id } = item;
        const isChecked = selectedItemsView.hasOwnProperty(id);
        callbackOnCheckboxClick?.(!isChecked, item);
        if (isChecked) {
            const { [id]: item, ...restItems } = selectedItemsView;
            setSelectedItemsView(restItems);
        } else {
            setSelectedItemsView({
                ...selectedItemsView,
                [id]: item,
            });
        }
    };
    const resetSelectedItemsView = () => setSelectedItemsView({});

    const createListItem = (item: IListItem, index: number) => {
        const { name, displayText, image, id, type } = item;
        const { isDisabled, isChecked, disabledTooltip } = getDisabledState({
            item: { id, type, isDisabled: item.isDisabled },
            selectedItemsView,
            selectionTypeProps,
        });
        // @ts-ignore FIXME: strict migration - 'selectionTypes' possibly undefined — optional on ISelectionTypeProps but always set via DEFAULT_SELECTION_TYPE_PROPS
        const isMultiSelect = selectionTypes[type] === ESelectionType.MULTIPLE;
        const defaultItemProps = {
            key: id + index,
            domain: displayText,
            name,
            img: image,
            isDisabled: isDisabled && isDisabledListItemSupported,
        };

        return (
            <Tooltip
                placement="top-start"
                content={disabledTooltip}
                enabled={isDisabled}
                key={id + index}
            >
                <div>
                    {isMultiSelect ? (
                        // @ts-ignore FIXME: strict migration - props not assignable to 'IListItemSegmentWithCheckbox' — item.name is optional (string | undefined) but ListItemSegment requires string; handled downstream via !!name
                        <ListItemSegmentWithCheckbox
                            isChecked={isChecked}
                            {...defaultItemProps}
                            onClick={() => onItemClickWithMultiselect(item)}
                        />
                    ) : (
                        // @ts-ignore FIXME: strict migration - props not assignable to 'IListItemSegment' — item.name is optional (string | undefined) but ListItemSegment requires string; handled downstream via !!name
                        <ListItemSegment
                            {...defaultItemProps}
                            onClick={renderItemsClick(item)}
                        />
                    )}
                </div>
            </Tooltip>
        );
    };
    const renderItems = ({ listItems, query }: FixmeType) => {
        const disableSegments = shouldDisableSegmentsTab && shouldDisableSegmentsTab();
        const { Segments: segments = [], AccountSegments: accountSegments = [] } = customSegmentsMeta;
        const { showAccountSegments = true } = tabsProps;
        const hasSegments = Boolean(showAccountSegments ? segments.length + accountSegments.length : segments.length);

        return (
            <Tabs
                listItems={listItems}
                createListItem={createListItem}
                hasQuery={hasQuery(query)}
                segmentsLoading={segmentsLoading}
                hasSegments={hasSegments}
                selectedMainAsset={selectedMainAsset}
                websiteResults={websiteResults}
                disableSegments={disableSegments}
                selectedItemsView={selectedItemsView}
                // @ts-ignore FIXME: strict migration - 'SelectionTypes | undefined' not assignable to 'SelectionTypes' — selectionTypes is optional on ISelectionTypeProps but always set via DEFAULT_SELECTION_TYPE_PROPS
                selectionTypes={selectionTypes}
                onApplyMultiSelect={onApplyMultiSelect}
                showLinesOfBusiness={showLinesOfBusiness}
                query={query}
                lobQuery={lobQuery}
                onItemClick={(item) => renderItemsClick(item)()}
                onItemClickWithMultiselect={onItemClickWithMultiselect}
                excludeItems={excludeItems}
                selectionTypeProps={selectionTypeProps}
                isDisabledListItemSupported={isDisabledListItemSupported}
                {...tabsProps}
            />
        );
    };
    const getListItemsWrapper = async (query: string) => {
        // Update LOB query immediately when debounced input fires
        setLobQuery(query ?? "");

        let websiteResults: FixmeType[] = [];
        if (hasQuery(query)) {
            try {
                setIsLoading(true);
                websiteResults = await getWebsiteResults(query);
            } finally {
                setIsLoading(false);
            }
        } else if (selectedMainAsset) {
            try {
                setIsLoading(true);
                const similarInfo = await fetchSimilarSitesQuery(
                    selectedMainAsset.type === EAssetType.Segment
                        ? selectedMainAsset.displayText
                        : selectedMainAsset.id,
                );
                websiteResults = similarInfo.map(({ Domain, Favicon }) => ({
                    name: Domain,
                    image: Favicon,
                }));
            } finally {
                setIsLoading(false);
            }
        }
        if (includeQueryAsItem) {
            websiteResults = getWebsiteResultsWithQuery(websiteResults, query);
        }
        setWebsiteResults(websiteResults);
        const listItems = getListItems(
            selectedValue,
            onClearSearch,
            renderItemsClick,
            query,
            customSegmentsMeta,
            excludeItems,
            websiteResults,
        );
        return listItems;
    };
    if (selectedValue) {
        const { image, displayText, name } = selectedValue;
        return (
            <FakeInputContainerStyled isValid={true}>
                <FakeInput
                    // @ts-ignore FIXME: strict migration - Argument 'undefined' not assignable to 'IAsset[] | IAsset' — clear action intentionally calls onSelect with no value
                    onClear={() => onSelect(undefined)}
                    closeButtonIconColor={closeButtonIconColor}
                >
                    <ListItemSegment
                        domain={displayText}
                        img={image}
                        // @ts-ignore FIXME: strict migration - 'string | undefined' not assignable to 'string' — selectedValue.name is optional but ListItemSegment requires string
                        name={name}
                    />
                </FakeInput>
            </FakeInputContainerStyled>
        );
    }

    const shouldRefresh = !segmentsLoading && customSegmentsMeta && Object.keys(customSegmentsMeta).length > 0;
    return (
        <AutocompleteStyled
            className={"AutocompleteWithTabs"}
            getListItems={getListItemsWrapper}
            renderItems={renderItems}
            preventTruncateUnlessForced={true}
            ref={autocompleteRef}
            loadingComponent={<DotsLoader />}
            shouldRefresh={shouldRefresh}
            floating={true}
            debounce={250}
            isLoading={isLoading}
            placeholder={placeholder}
            // @ts-ignore FIXME: strict migration - Property 'displayText' does not exist on type 'never' — selectedValue is narrowed to undefined after the early return above; value is always undefined here
            selectedValue={selectedValue?.displayText}
            autoFocus={autoFocus}
            disabled={disabled}
            searchIcon={searchIcon}
            hasBorder={hasBorder}
            onBlur={onBlur}
            {...autocompleteProps}
        />
    );
};
